import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Collapse,
  Icon,
  useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useColorMode } from '../context/ThemeContext';

const drawerWidth = 280;

function DrawerAppBar(props) {
  const { window: windowProp } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  const [openSubMenu, setOpenSubMenu] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const handleMenuClick = (event, menuKey) => {
    setAnchorEl({ ...anchorEl, [menuKey]: event.currentTarget });
  };

  const handleMenuClose = (menuKey) => {
    setAnchorEl({ ...anchorEl, [menuKey]: null });
  };

  const handleSubMenuToggle = (menuKey) => {
    setOpenSubMenu((prevState) => ({
      ...prevState,
      [menuKey]: !prevState[menuKey],
    }));
  };

  const handleInicioClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSectionNavigation = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToSection: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLegalNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemClick = (item) => {
    if (item.type === 'section') {
      handleSectionNavigation(item.section);
    } else if (item.type === 'route') {
      handleLegalNavigation(item.path);
    }
    setAnchorEl({});
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const menuItems = {
    app: {
      title: 'La App',
      items: [
        { label: '¿Qué es Mi Rifa?', section: 'que-es', type: 'section' },
        { label: 'Descargar App', section: 'descarga-app', type: 'section' },
        { label: 'Registro de Cuenta', section: 'registro', type: 'section' },
      ],
    },
    funciones: {
      title: 'Funciones',
      items: [
        { label: 'Crear tu Rifa', section: 'crea-tu-rifa', type: 'section' },
        { label: 'Vender Boletos', section: 'vender-boleto', type: 'section' },
        { label: 'Cambiar Estado', section: 'cambiar-estado', type: 'section' },
        { label: 'Estadísticas', section: 'estadisticas', type: 'section' },
        { label: 'Personalizar Tablero', section: 'personalizar', type: 'section' },
      ],
    },
    legal: {
      title: 'Legal',
      items: [
        { label: 'Políticas de Privacidad', path: '/politicas-privacidad', type: 'route' },
        { label: 'Términos y Condiciones', path: '/terminos-y-condiciones', type: 'route' },
        { label: 'Eliminación de Datos', path: '/eliminacion-de-datos', type: 'route' },
      ],
    },
  };

  const isActiveRoute = (path) => location.pathname === path;

  const drawer = (
    <Box
      sx={{
        textAlign: 'left',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.mode === 'dark' ? '#131b2e' : '#ffffff',
        color: theme.palette.text.primary,
      }}
    >
      {/* Header del Drawer con círculos decorativos */}
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#2d3484',
          py: 2.5,
          px: 2.5,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 140,
            height: 140,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            top: -40,
            right: -40,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            bottom: -20,
            left: 20,
          }}
        />
        <Box
          onClick={handleInicioClick}
          sx={{ cursor: 'pointer', zIndex: 1 }}
        >
          <img src="/assets/imgs/logo.png" alt="Mi Rifa App" style={{ height: '44px' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, zIndex: 1 }}>
          <IconButton
            onClick={toggleColorMode}
            size="small"
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '6px' }}
            title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          >
            <Icon sx={{ fontSize: 18 }}>{mode === 'dark' ? 'light_mode' : 'dark_mode'}</Icon>
          </IconButton>
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '6px' }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </Box>
      <Divider />
      <List sx={{ px: 1, py: 1.5, flex: 1, overflowY: 'auto' }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={handleInicioClick}
            sx={{
              borderRadius: '6px',
              backgroundColor: (location.pathname === '/' && !location.hash) ? (theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(45, 52, 132, 0.08)') : 'transparent',
              color: (location.pathname === '/' && !location.hash) ? (theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main) : theme.palette.text.primary,
              fontWeight: 700,
            }}
          >
            <Icon sx={{ mr: 1.5, fontSize: 20, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>home</Icon>
            <ListItemText primary="Inicio" primaryTypographyProps={{ fontWeight: 700, fontSize: '0.95rem', color: theme.palette.text.primary }} />
          </ListItemButton>
        </ListItem>

        {Object.entries(menuItems).map(([key, menu]) => (
          <Box key={key} sx={{ mb: 0.5 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleSubMenuToggle(key)} sx={{ borderRadius: '6px' }}>
                <ListItemText primary={menu.title} primaryTypographyProps={{ fontWeight: 700, fontSize: '0.95rem', color: theme.palette.text.primary }} />
                <Icon sx={{ color: theme.palette.text.secondary }}>{openSubMenu[key] ? 'expand_less' : 'expand_more'}</Icon>
              </ListItemButton>
            </ListItem>
            <Collapse in={openSubMenu[key]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {menu.items.map((item) => (
                  <ListItemButton
                    key={item.section || item.path}
                    onClick={() => handleItemClick(item)}
                    sx={{
                      py: 0.8,
                      my: 0.2,
                      borderRadius: '6px',
                      backgroundColor:
                        item.type === 'route' && isActiveRoute(item.path)
                          ? (theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(45, 52, 132, 0.08)')
                          : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.88rem',
                        fontWeight: item.type === 'route' && isActiveRoute(item.path) ? 700 : 500,
                        color:
                          item.type === 'route' && isActiveRoute(item.path)
                            ? (theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main)
                            : theme.palette.text.secondary,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>
    </Box>
  );

  const container = windowProp !== undefined ? () => windowProp().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        component="nav"
        elevation={0}
        sx={{
          borderRadius: '0px !important',
          border: 'none !important',
          borderBottom: 'none !important',
          backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#2d3484',
          boxShadow: theme.palette.mode === 'dark' ? '0 2px 14px rgba(0, 0, 0, 0.4)' : '0 2px 12px rgba(45, 52, 132, 0.12)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          overflow: 'hidden',
        }}
      >
        {/* Círculos decorativos orgánicos estilo rauh-rifas */}
        <Box
          sx={{
            position: 'absolute',
            width: 140,
            height: 140,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            top: -30,
            right: 40,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            bottom: -30,
            left: 120,
            pointerEvents: 'none',
          }}
        />

        <Toolbar sx={{ minHeight: { xs: 64, md: 70 }, px: { xs: 2, md: 4 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' },
              width: 38,
              height: 38,
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            }}
          >
            <Icon>menu</Icon>
          </IconButton>

          {/* Logo */}
          <Box
            onClick={handleInicioClick}
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            <img src="/assets/imgs/logo.png" alt="Mi Rifa App" style={{ height: '48px' }} loading="lazy" />
          </Box>

          {/* Navegación Desktop */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.8 }}>
            <Button
              color="inherit"
              onClick={handleInicioClick}
              sx={{
                px: 2,
                py: 0.8,
                borderRadius: '8px',
                fontWeight: (location.pathname === '/' && !location.hash) ? 800 : 600,
                fontSize: '0.92rem',
                color: '#ffffff',
                backgroundColor: (location.pathname === '/' && !location.hash) ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.14)',
                  color: '#ffffff',
                },
              }}
            >
              Inicio
            </Button>

            <Button
              color="inherit"
              onClick={(e) => handleMenuClick(e, 'app')}
              endIcon={<Icon sx={{ fontSize: '18px !important' }}>keyboard_arrow_down</Icon>}
              sx={{
                px: 2,
                py: 0.8,
                borderRadius: '8px',
                fontWeight: Boolean(anchorEl?.['app']) ? 800 : 600,
                fontSize: '0.92rem',
                color: '#ffffff',
                backgroundColor: Boolean(anchorEl?.['app']) ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.14)',
                  color: '#ffffff',
                },
              }}
            >
              La App
            </Button>

            <Button
              color="inherit"
              onClick={(e) => handleMenuClick(e, 'funciones')}
              endIcon={<Icon sx={{ fontSize: '18px !important' }}>keyboard_arrow_down</Icon>}
              sx={{
                px: 2,
                py: 0.8,
                borderRadius: '8px',
                fontWeight: Boolean(anchorEl?.['funciones']) ? 800 : 600,
                fontSize: '0.92rem',
                color: '#ffffff',
                backgroundColor: Boolean(anchorEl?.['funciones']) ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.14)',
                  color: '#ffffff',
                },
              }}
            >
              Funciones
            </Button>

            <Button
              color="inherit"
              onClick={(e) => handleMenuClick(e, 'legal')}
              endIcon={<Icon sx={{ fontSize: '18px !important' }}>keyboard_arrow_down</Icon>}
              sx={{
                px: 2,
                py: 0.8,
                borderRadius: '8px',
                fontWeight: (location.pathname === '/politicas-privacidad' || location.pathname === '/terminos-y-condiciones' || location.pathname === '/eliminacion-de-datos' || Boolean(anchorEl?.['legal'])) ? 800 : 600,
                fontSize: '0.92rem',
                color: '#ffffff',
                backgroundColor: (location.pathname === '/politicas-privacidad' || location.pathname === '/terminos-y-condiciones' || location.pathname === '/eliminacion-de-datos' || Boolean(anchorEl?.['legal'])) ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.14)',
                  color: '#ffffff',
                },
              }}
            >
              Legal
            </Button>

            {/* Botón de Modo Oscuro / Claro Desktop */}
            <IconButton
              onClick={toggleColorMode}
              sx={{
                ml: 1,
                p: 0.85,
                borderRadius: '8px',
                color: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.22)',
                  transform: 'scale(1.05)',
                },
              }}
              title={mode === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              <Icon sx={{ fontSize: 20 }}>{mode === 'dark' ? 'light_mode' : 'dark_mode'}</Icon>
            </IconButton>
          </Box>

          {/* Botón de Modo Oscuro / Claro Móvil */}
          <IconButton
            onClick={toggleColorMode}
            sx={{
              display: { sm: 'none' },
              color: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              p: 0.8,
              width: 38,
              height: 38,
            }}
            title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          >
            <Icon sx={{ fontSize: 20 }}>{mode === 'dark' ? 'light_mode' : 'dark_mode'}</Icon>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Menús desplegables: Estructura limpia y plana */}
      {Object.entries(menuItems).map(([key, menu]) => (
        <Menu
          key={key}
          anchorEl={anchorEl?.[key]}
          open={Boolean(anchorEl?.[key])}
          onClose={() => handleMenuClose(key)}
          MenuListProps={{
            'aria-labelledby': `${key}-button`,
          }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 210,
              borderRadius: '8px',
              bgcolor: theme.palette.background.paper,
              boxShadow: theme.palette.mode === 'dark' ? '0 12px 30px rgba(0, 0, 0, 0.6)' : '0 8px 24px rgba(0, 0, 0, 0.12)',
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
              p: 0.5,
            },
          }}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        >
          {menu.items.map((item) => (
            <MenuItem
              key={item.section || item.path}
              onClick={() => handleItemClick(item)}
              sx={{
                py: 1,
                px: 2,
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: item.type === 'route' && isActiveRoute(item.path) ? 700 : 500,
                color:
                  item.type === 'route' && isActiveRoute(item.path)
                    ? (theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main)
                    : theme.palette.text.primary,
                backgroundColor:
                  item.type === 'route' && isActiveRoute(item.path)
                    ? (theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(45, 52, 132, 0.08)')
                    : 'transparent',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9',
                  color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main,
                },
              }}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      ))}

      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: theme.palette.mode === 'dark' ? '#131b2e' : '#ffffff',
              color: theme.palette.text.primary,
              backgroundImage: 'none',
              borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}

DrawerAppBar.propTypes = {
  window: PropTypes.func,
};

export default DrawerAppBar;